import { useState, useEffect, useCallback, useRef } from "react";
import {
  adminApi,
  ApiError,
  AdminSupportTicket,
  AdminTicketMessage,
} from "../lib/api";
import {
  Loader2,
  RefreshCw,
  MessageSquare,
  Clock,
  Image,
  X,
  Send,
  User,
  Mail,
} from "lucide-react";
import { cn } from "../lib/utils";

const TOPIC_LABELS: Record<string, string> = {
  bug_report: "Bug Report",
  feature_request: "Feature Request",
  account_issue: "Account Issue",
  notifications: "Notifications",
  feedback: "Feedback",
  other: "Other",
};

const STATUS_COLORS: Record<string, string> = {
  open: "bg-amber-50 text-amber-700",
  in_progress: "bg-blue-50 text-blue-700",
  resolved: "bg-emerald-50 text-emerald-700",
  closed: "bg-slate-100 text-slate-500",
};

const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed"];

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

const MAX_ATTACHMENTS = 3;

export default function Support() {
  const [tickets, setTickets] = useState<AdminSupportTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Message thread state
  const [threadTicket, setThreadTicket] = useState<AdminSupportTicket | null>(
    null,
  );
  const [threadMessages, setThreadMessages] = useState<AdminTicketMessage[]>(
    [],
  );
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [replyPreviews, setReplyPreviews] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchTickets = useCallback(() => {
    setLoading(true);
    adminApi
      .supportTickets(filterStatus || undefined)
      .then((res) => {
        setTickets(res.data);
        setTotal(res.pagination.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filterStatus]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSelect = async (ticket: AdminSupportTicket) => {
    if (selectedId === ticket.id) return;
    setSelectedId(ticket.id);
    setThreadTicket(ticket);
    setThreadMessages([]);
    setReplyText("");
    setReplyAttachments([]);
    setReplyPreviews([]);
    setThreadLoading(true);
    try {
      const res = await adminApi.ticketMessages(ticket.id);
      setThreadMessages(res.messages);
    } catch {
      // Silently fail — ticket details still visible
    } finally {
      setThreadLoading(false);
    }
  };

  useEffect(() => {
    if (threadMessages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [threadMessages]);

  // Keep threadTicket in sync with ticket list updates
  useEffect(() => {
    if (selectedId) {
      const updated = tickets.find((t) => t.id === selectedId);
      if (updated) setThreadTicket(updated);
    }
  }, [tickets, selectedId]);

  const handleStatusChange = async (ticketId: number, newStatus: string) => {
    setUpdatingId(ticketId);
    try {
      const updated = await adminApi.updateTicketStatus(ticketId, newStatus);
      setTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, ...updated } : t)),
      );
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Failed to update ticket");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddReplyPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_ATTACHMENTS - replyAttachments.length;
    const toAdd = files.slice(0, remaining);
    setReplyAttachments((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setReplyPreviews((prev) => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeReplyAttachment = (idx: number) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== idx));
    setReplyPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSendReply = async (ticketId: number) => {
    if (!replyText.trim() && replyAttachments.length === 0) return;
    setSending(true);
    try {
      const msg = await adminApi.addTicketMessage(
        ticketId,
        replyText.trim(),
        replyAttachments.length > 0 ? replyAttachments : undefined,
      );
      setThreadMessages((prev) => [...prev, msg]);
      setReplyText("");
      setReplyAttachments([]);
      setReplyPreviews([]);
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId &&
          (t.status === "open" ||
            t.status === "resolved" ||
            t.status === "closed")
            ? { ...t, status: "in_progress" }
            : t,
        ),
      );
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Support & Feedback
          </h1>
          <p className="text-sm text-slate-500 mt-1">{total} total tickets</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-600"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
          <button
            onClick={fetchTickets}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Split panel */}
      <div className="flex gap-0 flex-1 min-h-0 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {/* Left: Ticket list */}
        <div className="w-95 shrink-0 border-r border-slate-200 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center flex-1">
              <Loader2 size={28} className="animate-spin text-slate-400" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-slate-400">
              <MessageSquare size={36} className="mb-3 opacity-40" />
              <p className="text-sm">No tickets found</p>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1">
              {tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => handleSelect(ticket)}
                  className={cn(
                    "w-full px-4 py-3.5 text-left border-b border-slate-100 transition-colors",
                    selectedId === ticket.id
                      ? "bg-blue-50/60"
                      : "hover:bg-slate-50/60",
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {ticket.userName}
                      </span>
                      <span
                        className={cn(
                          "text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full shrink-0",
                          STATUS_COLORS[ticket.status] || STATUS_COLORS.open,
                        )}
                      >
                        {ticket.status.replace("_", " ")}
                      </span>
                    </div>
                    <span className="text-[11px] text-slate-400 shrink-0 ml-2">
                      {timeAgo(ticket.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-semibold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded-full">
                      {TOPIC_LABELS[ticket.topic] || ticket.topic}
                    </span>
                    {ticket.attachments.length > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                        <Image size={10} />
                        {ticket.attachments.length}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate leading-relaxed">
                    {ticket.message}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Thread detail */}
        <div className="flex-1 flex flex-col min-w-0">
          {!selectedId ? (
            <div className="flex flex-col items-center justify-center flex-1 text-slate-400">
              <MessageSquare size={48} className="mb-3 opacity-20" />
              <p className="text-sm font-medium">
                Select a ticket to view the conversation
              </p>
            </div>
          ) : !threadTicket ? null : (
            <>
              {/* Thread header */}
              <div className="px-6 py-4 border-b border-slate-100 shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-slate-900">
                      {threadTicket.userName}
                    </h2>
                    <span
                      className={cn(
                        "text-[10px] font-bold uppercase px-2 py-0.5 rounded-full",
                        STATUS_COLORS[threadTicket.status] ||
                          STATUS_COLORS.open,
                      )}
                    >
                      {threadTicket.status.replace("_", " ")}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
                      {TOPIC_LABELS[threadTicket.topic] || threadTicket.topic}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Clock size={12} />
                    {formatDate(threadTicket.createdAt)}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <User size={12} />
                    {threadTicket.userName}
                  </span>
                  <span className="flex items-center gap-1">
                    <Mail size={12} />
                    {threadTicket.userEmail}
                  </span>
                  <span className="text-slate-300">#{threadTicket.id}</span>
                </div>
                {/* Status change */}
                <div className="flex items-center gap-2 mt-3">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      disabled={
                        threadTicket.status === s ||
                        updatingId === threadTicket.id
                      }
                      onClick={() => handleStatusChange(threadTicket.id, s)}
                      className={cn(
                        "px-3 py-1 rounded-lg text-xs font-semibold transition-colors capitalize",
                        threadTicket.status === s
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-40",
                      )}
                    >
                      {updatingId === threadTicket.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        s.replace("_", " ")
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {threadLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2
                      size={24}
                      className="animate-spin text-slate-400"
                    />
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Original message */}
                    <div className="flex justify-start mb-4">
                      <div className="max-w-[75%]">
                        <p className="text-[10px] font-semibold text-slate-400 mb-1">
                          {threadTicket.userName}
                        </p>
                        <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-slate-50 text-slate-800">
                          <p className="text-sm whitespace-pre-wrap">
                            {threadTicket.message}
                          </p>
                        </div>
                        {threadTicket.attachments.length > 0 && (
                          <div className="flex gap-2 mt-1.5">
                            {threadTicket.attachments.map((url, idx) => (
                              <button
                                key={idx}
                                onClick={() => setLightboxUrl(url)}
                                className="h-20 max-w-40 rounded-xl overflow-hidden border border-slate-200 hover:ring-2 hover:ring-blue-300 transition-all"
                              >
                                <img
                                  src={url}
                                  alt={`Attachment ${idx + 1}`}
                                  className="h-full w-auto object-contain"
                                />
                              </button>
                            ))}
                          </div>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">
                          {formatTime(threadTicket.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* Thread messages */}
                    {threadMessages.map((msg) => {
                      const isAdmin = msg.senderType === "admin";
                      return (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex mb-4",
                            isAdmin ? "justify-end" : "justify-start",
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[75%]",
                              isAdmin ? "text-right" : "text-left",
                            )}
                          >
                            <p className="text-[10px] font-semibold text-slate-400 mb-1">
                              {isAdmin
                                ? msg.senderName || "Admin"
                                : msg.senderName || "User"}
                            </p>
                            <div
                              className={cn(
                                "rounded-2xl px-4 py-3 text-left",
                                isAdmin
                                  ? "bg-blue-50 text-blue-900 rounded-br-md"
                                  : "bg-slate-50 text-slate-800 rounded-bl-md",
                              )}
                            >
                              <p className="text-sm whitespace-pre-wrap">
                                {msg.message}
                              </p>
                            </div>
                            {msg.attachments.length > 0 && (
                              <div
                                className={cn(
                                  "flex gap-2 mt-1.5",
                                  isAdmin ? "justify-end" : "justify-start",
                                )}
                              >
                                {msg.attachments.map((url, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => setLightboxUrl(url)}
                                    className="h-16 max-w-32 rounded-xl overflow-hidden border border-slate-200 hover:ring-2 hover:ring-blue-300 transition-all"
                                  >
                                    <img
                                      src={url}
                                      alt=""
                                      className="h-full w-auto object-contain"
                                    />
                                  </button>
                                ))}
                              </div>
                            )}
                            <p className="text-[10px] text-slate-400 mt-1">
                              {formatTime(msg.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Reply input */}
              <div className="px-6 py-3 border-t border-slate-100 shrink-0">
                {replyPreviews.length > 0 && (
                  <div className="flex gap-2 mb-2">
                    {replyPreviews.map((src, idx) => (
                      <div
                        key={idx}
                        className="relative size-12 rounded-xl overflow-hidden border border-slate-200"
                      >
                        <img
                          src={src}
                          alt=""
                          className="size-full object-cover"
                        />
                        <button
                          onClick={() => removeReplyAttachment(idx)}
                          className="absolute top-0.5 right-0.5 size-4 bg-black/60 rounded-full flex items-center justify-center"
                        >
                          <X size={8} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={replyAttachments.length >= MAX_ATTACHMENTS}
                    className="size-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-blue-500 hover:bg-slate-50 transition-colors disabled:opacity-30"
                  >
                    <Image size={16} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleAddReplyPhoto}
                  />
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && selectedId) {
                        e.preventDefault();
                        handleSendReply(selectedId);
                      }
                    }}
                    placeholder="Type a reply..."
                    rows={1}
                    className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 focus:ring-2 focus:ring-blue-300 focus:border-blue-300 outline-none transition-all resize-none max-h-24"
                  />
                  <button
                    onClick={() => selectedId && handleSendReply(selectedId)}
                    disabled={
                      sending ||
                      (!replyText.trim() && replyAttachments.length === 0)
                    }
                    className="size-9 rounded-lg bg-blue-500 text-white flex items-center justify-center disabled:opacity-40 transition-all hover:bg-blue-600"
                  >
                    {sending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Send size={14} />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 size-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X size={20} className="text-white" />
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment preview"
            className="max-w-full max-h-full rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
